import type { ReactNode } from 'react';
import { Copy, Download, Trash2 } from 'lucide-react';
import { Checkbox } from '@tes/ui';
import type { Selection } from '../../lib/useSelection';
import { useFormatDate } from '../../lib/useSettings';
import type { TemplateSummary } from '../../lib/types';
import { StatusBadges, type RowActions } from './TemplateCard';

export function TemplateTable({
  templates,
  sel,
  actionsFor,
}: {
  templates: TemplateSummary[];
  sel: Selection;
  actionsFor: (t: TemplateSummary) => RowActions;
}) {
  const fmt = useFormatDate();
  // Header cells carry the background themselves so the wrapper can round its
  // corners without `overflow-hidden` — which would clip nothing today, but
  // does clip any menu that later needs to escape a row.
  const th = 'bg-slate-50 px-3 py-2 font-medium';
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="text-left text-[11px] uppercase tracking-wider text-slate-400">
          <tr>
            <th className={`${th} w-10 rounded-tl-xl`}>
              <Checkbox
                checked={sel.allSelected}
                indeterminate={sel.someSelected}
                onToggle={() => sel.setAll(!sel.allSelected)}
                label={sel.allSelected ? 'Clear selection' : 'Select all templates'}
              />
            </th>
            <th className={th}>Name</th>
            <th className={th}>Status</th>
            <th className={th}>Updated</th>
            <th className={`${th} w-28 rounded-tr-xl`} />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {templates.map((t) => {
            const a = actionsFor(t);
            const selected = sel.has(t.id);
            return (
              <tr
                key={t.id}
                onClick={a.onOpen}
                className={`cursor-pointer ${selected ? 'bg-brand-50' : 'hover:bg-slate-50'}`}
              >
                <td className="px-3 py-2">
                  <Checkbox
                    checked={selected}
                    onToggle={(o) => sel.toggle(t.id, o)}
                    label={`Select ${t.name}`}
                  />
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{t.name}</div>
                  <div className="line-clamp-1 text-[11px] text-slate-400">
                    {t.description || t.id}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <StatusBadges template={t} />
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-slate-500" title={fmt.dateTime(t.updatedAt)}>
                  {fmt.date(t.updatedAt)}
                </td>
                <td className="px-3 py-2">
                  {/* Spelled out rather than hidden behind a ⋯ — a row has the
                      width for it, and it saves a click on the common actions. */}
                  <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    <RowAction label={`Duplicate ${t.name}`} onClick={a.onDuplicate}>
                      <Copy size={14} />
                    </RowAction>
                    <RowAction label={`Export ${t.name} as JSON`} onClick={a.onExport}>
                      <Download size={14} />
                    </RowAction>
                    <RowAction label={`Delete ${t.name}`} onClick={a.onDelete} danger>
                      <Trash2 size={14} />
                    </RowAction>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RowAction({
  label,
  onClick,
  danger = false,
  children,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`rounded p-1.5 ${
        danger ? 'text-red-500 hover:bg-red-50' : 'text-slate-500 hover:bg-slate-100'
      }`}
    >
      {children}
    </button>
  );
}
